import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, ChevronLeft, ChevronRight, Maximize2, Minimize2, Settings2, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { api } from '@/lib/api';
import type {
  RealtimeStatusAggregatorConfig,
  RealtimeStatusItemConfig,
  RealtimeStatusMetricConfig,
  RealtimeStatusSectionConfig,
  RealtimeStatusTabConfig,
  RealtimeStatusTone,
} from '@/lib/use-layout-config';

type StatusUser = {
  id?: string;
  role?: string;
  grants?: string[];
  enabledModules?: string[];
};

type RealtimeStatusSnapshot = {
  generatedAt?: string;
  tenantId?: string;
  metrics?: Record<string, string | number>;
  decisions?: Record<string, { title: string; reason?: string }>;
  sections?: Record<string, { items?: RealtimeStatusItemConfig[] }>;
};

type SetupRule = {
  roleRefs: string[];
  userWhitelist: string;
  userBlacklist: string;
  dateRangeDays: number;
  recordLimit: number;
};

interface RealtimeStatusAggregatorProps {
  config: RealtimeStatusAggregatorConfig | undefined;
  user: StatusUser | null | undefined;
  surface: 'web' | 'pwa';
}

const ADMIN_ROLES = new Set(['solution_admin', 'platform_admin', 'admin']);
const DEFAULT_SETUP_ROLES = ['manager', 'supervisor', 'operations', 'customer_service', 'finance', 'warehouse', 'driver'];

function normalized(values?: string[]): Set<string> {
  return new Set((values ?? []).map((value) => value.trim().toLowerCase()).filter(Boolean));
}

function hasPermission(user: StatusUser | null | undefined, permission?: string): boolean {
  if (!permission) return true;
  const role = (user?.role ?? '').toLowerCase();
  if (ADMIN_ROLES.has(role)) return true;
  const grants = normalized(user?.grants);
  if (grants.has('*') || grants.has(permission.toLowerCase())) return true;
  const [domain] = permission.toLowerCase().split('.');
  return Boolean(domain && (grants.has(`${domain}.*`) || grants.has(`${domain}:read`)));
}

function hasModule(user: StatusUser | null | undefined, moduleId?: string): boolean {
  if (!moduleId) return true;
  const enabled = normalized(user?.enabledModules);
  if (enabled.size === 0) return true;
  return enabled.has(moduleId.toLowerCase());
}

function canSeeTab(tab: RealtimeStatusTabConfig, user: StatusUser | null | undefined): boolean {
  return hasModule(user, tab.requiredModule) && hasPermission(user, tab.requiredPermission);
}

function canSeeSection(section: RealtimeStatusSectionConfig, user: StatusUser | null | undefined): boolean {
  const visibility = section.visibility;
  if (!visibility) return true;
  const userId = user?.id ?? '';
  if (visibility.userBlacklist?.includes(userId)) return false;
  if (visibility.userWhitelist && visibility.userWhitelist.length > 0 && !visibility.userWhitelist.includes(userId)) return false;
  if (!hasPermission(user, visibility.requiredPermission)) return false;
  if (!visibility.roleRefs || visibility.roleRefs.length === 0) return true;
  return visibility.roleRefs.map((role) => role.toLowerCase()).includes((user?.role ?? '').toLowerCase());
}

function canSetup(config: RealtimeStatusAggregatorConfig, user: StatusUser | null | undefined): boolean {
  if (!config.setup?.enabled) return false;
  const allowed = config.setup.allowedRoles.length > 0 ? config.setup.allowedRoles : [...ADMIN_ROLES];
  return allowed.map((role) => role.toLowerCase()).includes((user?.role ?? '').toLowerCase());
}

function toneClass(tone: RealtimeStatusTone | undefined): string {
  switch (tone) {
    case 'good':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'warn':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'bad':
      return 'border-red-200 bg-red-50 text-red-700';
    default:
      return 'border-border bg-muted/45 text-muted-foreground';
  }
}

function dotClass(tone: RealtimeStatusTone | undefined): string {
  switch (tone) {
    case 'good':
      return 'bg-emerald-500';
    case 'warn':
      return 'bg-amber-500';
    case 'bad':
      return 'bg-red-500';
    default:
      return 'bg-slate-400';
  }
}

function setupRuleFor(section: RealtimeStatusSectionConfig, existing?: SetupRule): SetupRule {
  return existing ?? {
    roleRefs: section.visibility?.roleRefs ?? [],
    userWhitelist: section.visibility?.userWhitelist?.join(', ') ?? '',
    userBlacklist: section.visibility?.userBlacklist?.join(', ') ?? '',
    dateRangeDays: section.scope?.dateRangeDays ?? 1,
    recordLimit: section.scope?.recordLimit ?? section.pageSize ?? 10,
  };
}

export function RealtimeStatusAggregator({ config, user, surface }: RealtimeStatusAggregatorProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<RealtimeStatusSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sectionPages, setSectionPages] = useState<Record<string, number>>({});
  const [detailPages, setDetailPages] = useState<Record<string, number>>({});
  const [hiddenSectionIds, setHiddenSectionIds] = useState<Set<string>>(() => new Set());
  const [draftTabs, setDraftTabs] = useState<RealtimeStatusTabConfig[]>([]);
  const [selectedItem, setSelectedItem] = useState<{ tab: RealtimeStatusTabConfig; section: RealtimeStatusSectionConfig; item: RealtimeStatusItemConfig } | null>(null);
  const [selectedSetup, setSelectedSetup] = useState<{ tab: RealtimeStatusTabConfig; section: RealtimeStatusSectionConfig } | null>(null);
  const [setupRules, setSetupRules] = useState<Record<string, SetupRule>>({});
  const [savingSetup, setSavingSetup] = useState(false);
  const [setupMessage, setSetupMessage] = useState<string | null>(null);

  useEffect(() => {
    setDraftTabs(config?.tabs ?? []);
    setActiveTabId((current) => current ?? config?.tabs.find((tab) => canSeeTab(tab, user))?.id ?? null);
  }, [config, user]);

  const visibleTabs = useMemo(
    () => draftTabs.filter((tab) => canSeeTab(tab, user)),
    [draftTabs, user],
  );
  const activeTab = visibleTabs.find((tab) => tab.id === activeTabId) ?? visibleTabs[0] ?? null;
  const setupAllowed = config ? canSetup(config, user) : false;

  const fetchSnapshot = useCallback(async () => {
    if (!config?.enabled || !config.snapshotEndpoint) return;
    try {
      const data = await api.get<RealtimeStatusSnapshot>(config.snapshotEndpoint);
      setSnapshot(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Snapshot unavailable');
    }
  }, [config]);

  useEffect(() => {
    if (!open || !config?.enabled) return;
    void fetchSnapshot();
    const timer = window.setInterval(() => void fetchSnapshot(), Math.max(5_000, config.pollMs));
    return () => window.clearInterval(timer);
  }, [config?.enabled, config?.pollMs, fetchSnapshot, open]);

  if (!config?.enabled || visibleTabs.length === 0) return null;

  const metrics = config.summaryMetrics.map((metric) => ({
    ...metric,
    value: snapshot?.metrics?.[metric.id] ?? metric.value ?? '-',
  }));

  const decision = activeTab
    ? snapshot?.decisions?.[activeTab.id] ?? activeTab.decision
    : undefined;

  function sectionItems(section: RealtimeStatusSectionConfig): RealtimeStatusItemConfig[] {
    const liveItems = snapshot?.sections?.[section.id]?.items;
    return liveItems && liveItems.length > 0 ? liveItems : section.items ?? [];
  }

  function patchSetupRule(section: RealtimeStatusSectionConfig, patch: Partial<SetupRule>) {
    setSetupRules((current) => ({
      ...current,
      [section.id]: {
        ...setupRuleFor(section, current[section.id]),
        ...patch,
      },
    }));
  }

  function addAiSuggestedSection() {
    if (!activeTab) return;
    const nextSection: RealtimeStatusSectionConfig = {
      id: `ai-suggested-${Date.now()}`,
      title: 'AI suggested watch list',
      type: 'feed',
      detailLevel: 'expanded',
      pageSize: 5,
      scope: { dateRangeDays: 7, recordLimit: 20, dataRef: 'tenant.ai.suggested_watch_list' },
      items: [
        { id: 'ai-watch-1', label: 'Review new blocker source', description: 'AI detected repeated waiting state in this tenant.', tone: 'warn' },
        { id: 'ai-watch-2', label: 'Add finance approval scope', description: 'Suggested for finance roles only.', tone: 'neutral' },
      ],
    };
    setDraftTabs((tabs) => tabs.map((tab) => (
      tab.id === activeTab.id
        ? { ...tab, sections: [...tab.sections, nextSection] }
        : tab
    )));
    setExpanded(true);
  }

  async function saveSetup() {
    if (!config) return;
    setSavingSetup(true);
    setSetupMessage(null);
    try {
      const nextTabs = draftTabs.map((tab) => ({
        ...tab,
        sections: tab.sections.map((section) => {
          const rule = setupRules[section.id];
          const whitelist = rule?.userWhitelist.split(',').map((value) => value.trim()).filter(Boolean);
          const blacklist = rule?.userBlacklist.split(',').map((value) => value.trim()).filter(Boolean);
          return {
            ...section,
            enabled: !hiddenSectionIds.has(section.id),
            pageSize: rule?.recordLimit ?? section.pageSize,
            scope: {
              ...section.scope,
              dateRangeDays: rule?.dateRangeDays ?? section.scope?.dateRangeDays,
              recordLimit: rule?.recordLimit ?? section.scope?.recordLimit,
            },
            visibility: {
              ...section.visibility,
              roleRefs: rule?.roleRefs ?? section.visibility?.roleRefs,
              userWhitelist: whitelist && whitelist.length > 0 ? whitelist : section.visibility?.userWhitelist,
              userBlacklist: blacklist && blacklist.length > 0 ? blacklist : section.visibility?.userBlacklist,
            },
          };
        }),
      }));
      const nextConfig: RealtimeStatusAggregatorConfig = {
        ...config,
        tabs: nextTabs,
      };
      await api.patch('/status-aggregator/config', { realtimeStatusAggregator: nextConfig });
      setSetupMessage('Saved to tenant config overlay.');
    } catch (err) {
      setSetupMessage(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingSetup(false);
    }
  }

  function renderSection(section: RealtimeStatusSectionConfig, tab: RealtimeStatusTabConfig) {
    if (section.enabled === false || hiddenSectionIds.has(section.id) || !canSeeSection(section, user)) return null;
    const items = sectionItems(section);
    const pageSize = Math.max(1, setupRules[section.id]?.recordLimit ?? section.pageSize ?? 5);
    const page = sectionPages[section.id] ?? 0;
    const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
    const visibleItems = items.slice(page * pageSize, page * pageSize + pageSize);
    const isExpandedOnly = section.detailLevel === 'expanded';

    return (
      <section
        key={section.id}
        className={`${isExpandedOnly && !expanded ? 'hidden' : ''} rounded-lg border border-border bg-card p-3 shadow-sm`}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{section.title}</h3>
            {section.scope ? (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {section.scope.dateRangeDays ?? setupRules[section.id]?.dateRangeDays ?? 1}d scope · {pageSize} rows/page
              </div>
            ) : null}
          </div>
          {setupMode ? (
            <button
              type="button"
              onClick={() => {
                setHiddenSectionIds((current) => {
                  const next = new Set(current);
                  if (next.has(section.id)) next.delete(section.id);
                  else next.add(section.id);
                  return next;
                });
              }}
              className="rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted"
            >
              {hiddenSectionIds.has(section.id) ? 'Enable' : 'Disable'}
            </button>
          ) : null}
        </div>

        <div className="space-y-2">
          {visibleItems.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">No items in current scope.</div>
          ) : visibleItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedItem({ tab, section, item })}
              className="grid w-full grid-cols-[auto_1fr_auto] items-start gap-2 rounded-md border border-border bg-background p-2 text-left hover:bg-muted/55"
            >
              <span className={`mt-1 h-2.5 w-2.5 rounded-full ${dotClass(item.tone)}`} />
              <span className="min-w-0">
                <span className="block truncate text-xs font-semibold text-foreground">{item.label}</span>
                {item.description ? <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">{item.description}</span> : null}
                {typeof item.progressPct === 'number' ? (
                  <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-muted">
                    <span className="block h-full rounded-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, item.progressPct))}%` }} />
                  </span>
                ) : null}
              </span>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${toneClass(item.tone)}`}>
                {item.actionLabel ?? item.value ?? (typeof item.progressPct === 'number' ? `${item.progressPct}%` : 'View')}
              </span>
            </button>
          ))}
        </div>

        {items.length > pageSize ? (
          <div className="mt-3 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
            <span>Page {page + 1} / {pageCount}</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setSectionPages((current) => ({ ...current, [section.id]: Math.max(0, page - 1) }))}
                className="rounded-md border border-border p-1 disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                disabled={page + 1 >= pageCount}
                onClick={() => setSectionPages((current) => ({ ...current, [section.id]: Math.min(pageCount - 1, page + 1) }))}
                className="rounded-md border border-border p-1 disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ) : null}
      </section>
    );
  }

  function renderDetailPanel() {
    if (!selectedItem) return null;
    const detailKey = `${selectedItem.section.id}:${selectedItem.item.id}`;
    const details = selectedItem.item.details ?? [];
    const pageSize = Math.max(1, selectedItem.section.scope?.recordLimit ?? selectedItem.section.pageSize ?? 5);
    const page = detailPages[detailKey] ?? 0;
    const pageCount = Math.max(1, Math.ceil(details.length / pageSize));
    const visibleDetails = details.slice(page * pageSize, page * pageSize + pageSize);

    return (
      <aside
        className="absolute bottom-0 top-0 w-[min(380px,92vw)] border-r border-border bg-background shadow-2xl"
        style={{
          right: expanded ? 'min(920px, 92vw)' : surface === 'pwa' ? 'min(390px, 100vw)' : '410px',
        }}
      >
        <div className="flex items-start justify-between gap-2 border-b border-border p-3">
          <div className="min-w-0">
            <div className="truncate text-[11px] text-muted-foreground">{selectedItem.tab.label} / {selectedItem.section.title}</div>
            <h3 className="mt-1 truncate text-sm font-semibold text-foreground">{selectedItem.item.label}</h3>
          </div>
          <button type="button" onClick={() => setSelectedItem(null)} className="rounded-md border border-border p-1.5 text-muted-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 space-y-3 overflow-y-auto p-3 text-sm">
          <div className={`rounded-md border p-3 ${toneClass(selectedItem.item.tone)}`}>
            {selectedItem.item.description ?? 'No detail description configured.'}
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                <SlidersHorizontal className="h-4 w-4" />
                Detail list
              </div>
              <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
                {details.length} rows
              </span>
            </div>
            <div className="mt-3 space-y-2">
              {visibleDetails.length > 0 ? visibleDetails.map((detail) => (
                <div key={detail.id} className="grid grid-cols-[auto_1fr_auto] gap-2 rounded-md border border-border bg-background p-2">
                  <span className={`mt-1.5 h-2.5 w-2.5 rounded-full ${dotClass(detail.tone)}`} />
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold text-foreground">{detail.label}</div>
                    {detail.description ? <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{detail.description}</div> : null}
                    {detail.timestamp ? <div className="mt-1 text-[10px] text-muted-foreground">{new Date(detail.timestamp).toLocaleString()}</div> : null}
                  </div>
                  {detail.value !== undefined ? <div className={`h-fit rounded-full border px-2 py-0.5 text-[10px] font-semibold ${toneClass(detail.tone)}`}>{detail.value}</div> : null}
                </div>
              )) : (
                <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
                  No paged records returned for this item.
                </div>
              )}
            </div>
            {details.length > pageSize ? (
              <div className="mt-3 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
                <span>Page {page + 1} / {pageCount}</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={page === 0}
                    onClick={() => setDetailPages((current) => ({ ...current, [detailKey]: Math.max(0, page - 1) }))}
                    className="rounded-md border border-border p-1 disabled:opacity-40"
                    aria-label="Previous detail page"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={page + 1 >= pageCount}
                    onClick={() => setDetailPages((current) => ({ ...current, [detailKey]: Math.min(pageCount - 1, page + 1) }))}
                    className="rounded-md border border-border p-1 disabled:opacity-40"
                    aria-label="Next detail page"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
              <SlidersHorizontal className="h-4 w-4" />
              Data scope
            </div>
            <dl className="mt-2 space-y-1 text-xs text-muted-foreground">
              <div className="flex justify-between gap-3"><dt>Tenant</dt><dd>current only</dd></div>
              <div className="flex justify-between gap-3"><dt>Section</dt><dd>{selectedItem.section.id}</dd></div>
              <div className="flex justify-between gap-3"><dt>Date days</dt><dd>{selectedItem.section.scope?.dateRangeDays ?? setupRules[selectedItem.section.id]?.dateRangeDays ?? 1}</dd></div>
              <div className="flex justify-between gap-3"><dt>Record limit</dt><dd>{selectedItem.section.scope?.recordLimit ?? setupRules[selectedItem.section.id]?.recordLimit ?? selectedItem.section.pageSize ?? 5}</dd></div>
            </dl>
          </div>
        </div>
      </aside>
    );
  }

  function renderSetupList() {
    if (!setupMode || !activeTab) return null;
    return (
      <section className="rounded-lg border border-border bg-card p-3 shadow-sm">
        <div className="mb-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Panel setup</h3>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Configure sections as list items. Click a row to edit scope and visibility.</p>
          </div>
        </div>
        {setupMessage ? <div className="mt-2 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground">{setupMessage}</div> : null}

        <div className="space-y-2">
          {activeTab.sections.map((section) => {
            const rule = setupRuleFor(section, setupRules[section.id]);
            const hidden = hiddenSectionIds.has(section.id) || section.enabled === false;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => {
                  setSelectedItem(null);
                  setSelectedSetup({ tab: activeTab, section });
                }}
                className="grid w-full grid-cols-[auto_1fr_auto] items-start gap-2 rounded-md border border-border bg-background p-2 text-left hover:bg-muted/55"
              >
                <span className={`mt-1 h-2.5 w-2.5 rounded-full ${hidden ? 'bg-slate-300' : 'bg-emerald-500'}`} />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-semibold text-foreground">{section.title}</span>
                  <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
                    {section.type} · {rule.dateRangeDays}d · {rule.recordLimit} rows/page · {rule.roleRefs.length > 0 ? `${rule.roleRefs.length} roles` : 'all roles'}
                  </span>
                </span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${hidden ? toneClass('neutral') : toneClass('good')}`}>
                  {hidden ? 'Disabled' : 'Configure'}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  function renderSetupDetailPanel() {
    if (!selectedSetup) return null;
    const { section, tab } = selectedSetup;
    const rule = setupRuleFor(section, setupRules[section.id]);
    const hidden = hiddenSectionIds.has(section.id) || section.enabled === false;

    return (
      <section className="space-y-3 text-sm">
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="truncate text-[11px] text-muted-foreground">Setup / {tab.label}</div>
          <h3 className="mt-1 truncate text-sm font-semibold text-foreground">{section.title}</h3>
        </div>

          <div className="rounded-md border border-border bg-card p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold text-foreground">Visibility</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{section.type}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setHiddenSectionIds((current) => {
                    const next = new Set(current);
                    if (next.has(section.id)) next.delete(section.id);
                    else next.add(section.id);
                    return next;
                  });
                }}
                className={`rounded-md border px-2 py-1 text-[11px] font-semibold ${hidden ? toneClass('neutral') : toneClass('good')}`}
              >
                {hidden ? 'Enable' : 'Disable'}
              </button>
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs font-semibold text-foreground">Data scope</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              <label className="space-y-1">
                <span className="text-muted-foreground">Date days</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={rule.dateRangeDays}
                  onChange={(event) => patchSetupRule(section, { dateRangeDays: Number(event.target.value) || 1 })}
                  className="h-8 w-full rounded-md border border-border bg-background px-2"
                />
              </label>
              <label className="space-y-1">
                <span className="text-muted-foreground">Rows/page</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={rule.recordLimit}
                  onChange={(event) => patchSetupRule(section, { recordLimit: Number(event.target.value) || 5 })}
                  className="h-8 w-full rounded-md border border-border bg-background px-2"
                />
              </label>
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs font-semibold text-foreground">Roles</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {DEFAULT_SETUP_ROLES.map((role) => {
                const enabled = rule.roleRefs.includes(role);
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => patchSetupRule(section, {
                      roleRefs: enabled ? rule.roleRefs.filter((item) => item !== role) : [...rule.roleRefs, role],
                    })}
                    className={`rounded-full border px-2 py-0.5 text-[10px] ${enabled ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-muted-foreground'}`}
                  >
                    {role}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs font-semibold text-foreground">User scope</div>
            <div className="mt-3 grid grid-cols-1 gap-2 text-[11px]">
              <label className="space-y-1">
                <span className="text-muted-foreground">User whitelist</span>
                <input
                  value={rule.userWhitelist}
                  onChange={(event) => patchSetupRule(section, { userWhitelist: event.target.value })}
                  placeholder="user ids"
                  className="h-8 w-full rounded-md border border-border bg-background px-2"
                />
              </label>
              <label className="space-y-1">
                <span className="text-muted-foreground">User blacklist</span>
                <input
                  value={rule.userBlacklist}
                  onChange={(event) => patchSetupRule(section, { userBlacklist: event.target.value })}
                  placeholder="user ids"
                  className="h-8 w-full rounded-md border border-border bg-background px-2"
                />
              </label>
            </div>
          </div>
      </section>
    );
  }

  function renderSetupLayer() {
    if (!setupMode) return null;
    return (
      <div className="absolute inset-0 z-20 flex flex-col bg-background shadow-2xl">
        <div className="border-b border-border bg-background p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {selectedSetup ? (
                  <button
                    type="button"
                    onClick={() => setSelectedSetup(null)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground"
                    aria-label="Back to setup list"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                ) : null}
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-foreground">
                    {selectedSetup ? 'Section setup' : 'Panel setup'}
                  </h2>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Separate top layer over status · current tenant config
                  </p>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {config?.setup?.aiAssistEnabled && !selectedSetup ? (
                <button
                  type="button"
                  onClick={addAiSuggestedSection}
                  className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] font-semibold text-foreground"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  AI add
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void saveSetup()}
                disabled={savingSetup}
                className="h-8 rounded-md bg-primary px-3 text-[11px] font-semibold text-primary-foreground disabled:opacity-60"
              >
                {savingSetup ? 'Saving' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSetupMode(false);
                  setSelectedSetup(null);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground"
                aria-label="Close setup"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/20 p-3">
          {selectedSetup ? renderSetupDetailPanel() : renderSetupList()}
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        title="Realtime status"
      >
        <Activity className="h-4 w-4" />
        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-background" />
      </button>

      {open ? (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Close realtime status"
            className="absolute inset-0 bg-slate-950/20"
            onClick={() => {
              setOpen(false);
              setExpanded(false);
              setSetupMode(false);
              setSelectedItem(null);
              setSelectedSetup(null);
            }}
          />
          <aside
            className={`absolute right-0 top-0 flex h-full flex-col border-l border-border bg-background shadow-2xl transition-[width] duration-200 ${
              expanded ? 'w-[min(920px,92vw)]' : surface === 'pwa' ? 'w-[min(390px,100vw)]' : 'w-[410px]'
            }`}
          >
            <div className="border-b border-border bg-background p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-foreground">{config.title}</h2>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Current tenant only · {snapshot?.generatedAt ? new Date(snapshot.generatedAt).toLocaleTimeString() : 'live snapshot'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {setupAllowed ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSetupMode((value) => !value);
                        setSelectedItem(null);
                        setSelectedSetup(null);
                        setExpanded(true);
                      }}
                      className={`inline-flex h-8 items-center gap-1 rounded-md border px-2 text-[11px] font-semibold ${setupMode ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground'}`}
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                      Setup
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setExpanded((value) => !value)}
                    className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] font-semibold text-foreground"
                  >
                    {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                    {expanded ? 'Narrow' : 'Expand'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      setSetupMode(false);
                      setSelectedItem(null);
                      setSelectedSetup(null);
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className={`mt-3 grid gap-2 ${expanded ? 'grid-cols-5' : 'grid-cols-2'}`}>
                {metrics
                  .filter((metric: RealtimeStatusMetricConfig) => hasPermission(user, metric.requiredPermission))
                  .slice(0, expanded ? 5 : 2)
                  .map((metric) => (
                    <div key={metric.id} className="rounded-md border border-border bg-card p-2">
                      <div className="truncate text-[10px] text-muted-foreground">{metric.label}</div>
                      <div className="mt-1 truncate text-sm font-semibold text-foreground">{metric.value}</div>
                    </div>
                  ))}
              </div>

              <div className="mt-3 flex gap-1 overflow-x-auto">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setActiveTabId(tab.id);
                      setSelectedItem(null);
                      setSelectedSetup(null);
                    }}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${activeTab?.id === tab.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-muted-foreground'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-muted/20 p-3">
              {error ? (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Live snapshot fallback: {error}
                </div>
              ) : null}

              {decision ? (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="text-[10px] font-bold uppercase text-amber-700">Best decision now</div>
                  <div className="mt-1 text-sm font-semibold leading-5 text-foreground">{decision.title}</div>
                  {decision.reason ? <p className="mt-1 text-xs leading-5 text-amber-800">{decision.reason}</p> : null}
                </div>
              ) : null}

              {activeTab ? (
                <div className={`mt-3 grid gap-3 ${expanded ? 'grid-cols-[minmax(0,1.1fr)_minmax(280px,0.9fr)]' : 'grid-cols-1'}`}>
                  <div className="space-y-3">
                    {activeTab.sections
                      .filter((section) => section.detailLevel !== 'expanded')
                      .map((section) => renderSection(section, activeTab))}
                  </div>
                  <div className="space-y-3">
                    {activeTab.sections
                      .filter((section) => section.detailLevel === 'expanded')
                      .map((section) => renderSection(section, activeTab))}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">No status tabs configured for this role.</div>
              )}
            </div>

            {renderDetailPanel()}
            {renderSetupLayer()}
          </aside>
        </div>
      ) : null}
    </>
  );
}
